import React, { FunctionComponent, useCallback, useContext, useMemo } from 'react';
import moment from 'moment';
import { compact } from 'lodash';
import Translate from 'ui/atoms/translate';
import Date from 'ui/atoms/date';
import Currency from 'ui/atoms/currency';
import Button from 'ui/atoms/button';
import DataRecordTable, { CellTypeEnum, DataRecordTableCell } from 'libraries/data-record-table';
import {
  currentTokenHasManageApprovalsPermission,
  currentTokenHasManageCreditDigitalSecuritiesPermission,
  currentTokenHasManageIncomingPaymentsPermission,
} from 'src/apps/issuer/helpers/tokenPermissions';
import { DataRecordOrdering } from 'ui/types/data-ordering';

import { AdminInvestmentList, AdminInvestmentToken } from 'api/models';
import ActionButtons from 'ui/molecules/action-buttons';
import { PaginationProps } from 'ui/molecules/pagination';
import { I18NContext } from 'src/ui/i18n/provider';
import {
  AdminApi,
  AdminInvestmentsListOrderingEnum,
  AdminInvestmentsListStatusEnum,
  AdminInvestmentsValuesRetrieveFieldEnum,
} from 'src/api';
import useApiCall from 'src/hooks/use-api-call';
import { Search } from 'src/ui/molecules/filtering';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';

type TableType = 'approveOffers' | 'confirmPayments' | 'creditSecurities';

export interface StudioConfirmInvestmentsTableProps {
  type: TableType;
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  investments?: AdminInvestmentList[];
  loadingConfirmationId?: string;
  onConfirm: (investmentId: string) => void;
  onOpenCancellationModal: (investmentId: string) => void;
  loading?: boolean;
  paginationProps?: PaginationProps;
  hasPermission: boolean;
  setFilterPopupHeight?: (height: number) => void;
}

const StudioConfirmInvestmentsTable: FunctionComponent<StudioConfirmInvestmentsTableProps> = ({
  type,
  ordering,
  onOrderBy,
  investments,
  loadingConfirmationId,
  onConfirm,
  onOpenCancellationModal,
  paginationProps,
  loading,
  hasPermission,
  setFilterPopupHeight,
}) => {
  const { activeLocale } = useContext(I18NContext);
  const { makeAuthenticatedApi } = useApiCall();
  const translate = useTranslateWithStringArgs();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const actionColumnWidths: Record<string, Record<TableType, number>> = {
    en: {
      approveOffers: 166,
      confirmPayments: 166,
      creditSecurities: 98,
    },
    de: {
      approveOffers: 194,
      confirmPayments: 210,
      creditSecurities: 142,
    },
  };
  const actionColumnWidth = actionColumnWidths[activeLocale][type as TableType];

  const hasPermissionsForAction = useCallback(
    (type: TableType, token: AdminInvestmentToken) => {
      switch (type) {
        case 'approveOffers':
          return currentTokenHasManageApprovalsPermission(token);
        case 'confirmPayments':
          return currentTokenHasManageIncomingPaymentsPermission(token);
        case 'creditSecurities':
          return currentTokenHasManageCreditDigitalSecuritiesPermission(token);
        default:
          return false;
      }
    },
    [type],
  );

  return (
    <DataRecordTable
      onOrderBy={onOrderBy}
      ordering={ordering}
      data={investments}
      loading={loading}
      paginationProps={paginationProps}
      hasPermission={hasPermission}
      setFilterPopupHeight={setFilterPopupHeight}
      searchComponent={
        <Search input="search" defaultField="search" label={translate('filters.labels.investments')} name="search" />
      }
      cells={compact([
        {
          title: <Translate name={`studioConfirmInvestmentsTable.${type}.product`} />,
          render: (investment) => investment.productName,
          orderingFieldName: AdminInvestmentsListOrderingEnum.PRODUCT_NAME_ASC,
          type: CellTypeEnum.text,
          filterName: 'productName',
          searchFieldName: AdminInvestmentsValuesRetrieveFieldEnum.PRODUCT_NAME,
          getFilterData: (params: any) =>
            adminApi.adminInvestmentsValuesRetrieve({
              ...params,
              status: {
                approveOffers: AdminInvestmentsListStatusEnum.ACCEPTABLE,
                confirmPayments: AdminInvestmentsListStatusEnum.PAYMENT_OUTSTANDING,
                creditSecurities: AdminInvestmentsListStatusEnum.SECURITIES_OUTSTANDING,
              }[type],
            }),
        },
        {
          title: <Translate name={`studioConfirmInvestmentsTable.${type}.investorName`} />,
          render: (investment) => investment.investorName,
          orderingFieldName: AdminInvestmentsListOrderingEnum.INVESTOR_NAME_ASC,
          type: CellTypeEnum.text,
          filterName: 'investorName',
          searchFieldName: AdminInvestmentsValuesRetrieveFieldEnum.INVESTOR_NAME,
          getFilterData: (params: any) =>
            adminApi.adminInvestmentsValuesRetrieve({
              ...params,
              status: {
                approveOffers: AdminInvestmentsListStatusEnum.ACCEPTABLE,
                confirmPayments: AdminInvestmentsListStatusEnum.PAYMENT_OUTSTANDING,
                creditSecurities: AdminInvestmentsListStatusEnum.SECURITIES_OUTSTANDING,
              }[type],
            }),
        },
        {
          title: <Translate name={`studioConfirmInvestmentsTable.${type}.amount`} args={[<>&shy;</>]} />,
          alignContent: 'right',
          type: CellTypeEnum.currency,
          filterName: {
            approveOffers: 'investmentTotal',
            creditSecurities: 'investmentTotal',
            confirmPayments: 'paymentTotal',
          }[type],
          render: (investment) => (
            <Currency>
              {
                {
                  approveOffers: investment.investmentTotal,
                  creditSecurities: investment.investmentTotal,
                  confirmPayments: investment.paymentTotal,
                }[type]
              }
            </Currency>
          ),
          orderingFieldName:
            type === 'approveOffers' || type === 'creditSecurities'
              ? AdminInvestmentsListOrderingEnum.INVESTMENT_TOTAL_ASC
              : AdminInvestmentsListOrderingEnum.PAYMENT_TOTAL_ASC,
        },
        type === 'confirmPayments' && {
          title: <Translate name="studioConfirmInvestmentsTable.confirmPayments.paymentDeadline" />,
          render: (investment) => investment.paymentDeadline && <Date>{investment.paymentDeadline}</Date>,
          orderingFieldName: AdminInvestmentsListOrderingEnum.PAYMENT_DEADLINE_ASC,
          type: CellTypeEnum.date,
          filterName: 'paymentDeadline',
        },
        type === 'creditSecurities' && {
          title: <Translate name="studioConfirmInvestmentsTable.creditSecurities.units" args={[<>&shy;</>]} />,
          alignContent: 'right',
          render: (investment) => investment.remainingAmount,
          orderingFieldName: AdminInvestmentsListOrderingEnum.FULFILLED_AMOUNT_ASC,
          type: CellTypeEnum.number,
          filterName: 'fulfilledAmount',
        },
        type === 'confirmPayments' && {
          title: <Translate name="studioConfirmInvestmentsTable.confirmPayments.referenceText" args={[<>&shy;</>]} />,
          alignContent: 'left',
          render: (investment) => investment.paymentCode,
          orderingFieldName: AdminInvestmentsListOrderingEnum.PAYMENT_CODE_ASC,
          type: CellTypeEnum.text,
          filterName: 'paymentCode',
          searchFieldName: AdminInvestmentsValuesRetrieveFieldEnum.PAYMENT_CODE,
          getFilterData: (params: any) =>
            adminApi.adminInvestmentsValuesRetrieve({
              ...params,
              status: {
                approveOffers: AdminInvestmentsListStatusEnum.ACCEPTABLE,
                confirmPayments: AdminInvestmentsListStatusEnum.PAYMENT_OUTSTANDING,
                creditSecurities: AdminInvestmentsListStatusEnum.SECURITIES_OUTSTANDING,
              }[type],
            }),
        },
        {
          title: <Translate name={`studioConfirmInvestmentsTable.${type}.actionHeader`} />,
          alignContent: 'left',
          width: actionColumnWidth,
          render: (investment) => (
            <ActionButtons align="start" spacingTop="none">
              {((type === 'approveOffers' && investment.canCancel.length) || type === 'confirmPayments') && (
                <Button
                  variant="link"
                  color="primary"
                  disabled={
                    (type === 'confirmPayments' && moment().isSameOrBefore(investment.paymentDeadline, 'day')) ||
                    !hasPermissionsForAction(type, investment.token)
                  }
                  onClick={() => onOpenCancellationModal(investment.id || '')}
                  size="xsmall"
                >
                  <Translate name={`studioConfirmInvestmentsTable.${type}.cancel`} />
                </Button>
              )}
              <Button
                disabled={
                  (type === 'creditSecurities' && !investment.canIssue) ||
                  !hasPermissionsForAction(type, investment.token)
                }
                onClick={() => onConfirm(investment.id || '')}
                variant="secondary"
                loading={investment.id === loadingConfirmationId}
                size="xsmall"
              >
                <Translate name={`studioConfirmInvestmentsTable.${type}.action`} />
              </Button>
            </ActionButtons>
          ),
        },
      ] as DataRecordTableCell<AdminInvestmentList>[])}
    />
  );
};

export default StudioConfirmInvestmentsTable;
